import React, { useId, useRef, useEffect, useState } from "react";
import { readQrFromImageData } from "../../../utils/scan/qr";
import {
  MAX_CANVAS_SIZE,
  CARD_WIDTH_PCT,
  CARD_RATIO,
  QR_ZONE_SIZE_PCT,
  OCR_LEFT_MARGIN_PCT,
  OCR_HEIGHT_PCT,
  BORDER_SHIFT_PCT,
  OCR_ZONE_WIDTH_PCT,
  QR_ZONE_WIDTH_PCT,
} from "../../../utils/scan/cardGeometry";

const SCAN_INTERVAL_MS = 200;
const EXTRA_FRAMES_FOR_OCR = 9;
const EXTRA_FRAME_DELAY_MS = 80;
const NO_QR_TIMEOUT_MS = 3000;
const NO_QR_TIMEOUT_SEC = Math.ceil(NO_QR_TIMEOUT_MS / 1000);
const LOG_PREFIX = "[Live QR]";
const toSecs = (ms: number): string => `${(ms / 1000).toFixed(2)}s`;

interface Props {
  onQrFound: (frameDataUrl: string, qrUrl: string, extraFrames?: string[]) => void;
  /** Called when no QR found after NO_QR_TIMEOUT_MS: captures frames and runs OCR only. */
  onTimeoutNoQr?: (mainFrameDataUrl: string, extraFrames: string[]) => void;
  onFallbackPhoto?: () => void;
}

/**
 * Live camera stream that continuously scans for a QR code.
 * When found, captures the current frame and calls onQrFound(frameDataUrl, qrUrl).
 */
const LiveQrScanner = ({ onQrFound, onTimeoutNoQr, onFallbackPhoto }: Props) => {
  const scanMaskId = useId().replace(/:/g, "");
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const ocrIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const countdownIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const countdownEndTimeRef = useRef<number | null>(null);
  const completedRef = useRef(false);
  const ocrFramesRef = useRef<string[]>([]);
  const cardFrameRef = useRef<HTMLDivElement>(null);
  const qrUrlRef = useRef<string | null>(null);
  const detectionStartedAtRef = useRef<number>(0);
  const [error, setError] = useState<string | null>(null);
  const [streamReady, setStreamReady] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState<number | null>(null);
  const [detectionStarted, setDetectionStarted] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const startStream = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: { ideal: "environment" },
            width: { ideal: 1920 },
            height: { ideal: 1080 },
          },
        });
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        const video = videoRef.current;
        if (!video) return;
        video.srcObject = stream;
        const onReady = () => {
          if (cancelled) return;
          if (video.videoWidth > 0 && video.videoHeight > 0) {
            setStreamReady(true);
          }
        };
        video.onloadeddata = onReady;
        video.oncanplay = onReady;
        await video.play();
        if (video.videoWidth > 0 && video.videoHeight > 0 && !cancelled) {
          setStreamReady(true);
        }
      } catch (e) {
        if (!cancelled) setError("Impossible d'accéder à la caméra");
      }
    };

    startStream();
    return () => {
      cancelled = true;
      setStreamReady(false);
      setSecondsLeft(null);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
      if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (ocrIntervalRef.current) clearInterval(ocrIntervalRef.current);
      streamRef.current?.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    };
  }, []);

  const onQrFoundRef = useRef(onQrFound);
  onQrFoundRef.current = onQrFound;
  const onTimeoutNoQrRef = useRef(onTimeoutNoQr);
  onTimeoutNoQrRef.current = onTimeoutNoQr;

  const flashCardFrame = () => {
    const el = cardFrameRef.current;
    if (!el) return;
    el.classList.remove("card-frame-capture-flash");
    void el.offsetWidth;
    el.classList.add("card-frame-capture-flash");
  };

  useEffect(() => {
    if (!streamReady || error || !detectionStarted) return;

    const detectionStartPerf = performance.now();
    console.log(`${LOG_PREFIX} detection started`);
    completedRef.current = false;
    qrUrlRef.current = null;
    ocrFramesRef.current = [];
    detectionStartedAtRef.current = Date.now();
    countdownEndTimeRef.current = Date.now() + NO_QR_TIMEOUT_MS;
    setSecondsLeft(NO_QR_TIMEOUT_SEC);
    if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
    countdownIntervalRef.current = setInterval(() => {
      if (completedRef.current) {
        if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
        setSecondsLeft(null);
        return;
      }
      const end = countdownEndTimeRef.current;
      if (end == null) return;
      const left = Math.max(0, Math.ceil((end - Date.now()) / 1000));
      setSecondsLeft(left);
    }, 1000);

    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const ocrFramesTarget = EXTRA_FRAMES_FOR_OCR + 1;

    const captureFrame = () => {
      if (video.readyState !== video.HAVE_ENOUGH_DATA || video.videoWidth === 0) return "";
      const vw = video.videoWidth;
      const vh = video.videoHeight;
      const scale = Math.min(1, MAX_CANVAS_SIZE / Math.max(vw, vh));
      const w = Math.round(vw * scale);
      const h = Math.round(vh * scale);
      canvas.width = w;
      canvas.height = h;
      ctx.drawImage(video, 0, 0, vw, vh, 0, 0, w, h);
      return canvas.toDataURL("image/jpeg", 0.95);
    };

    const stopAllTimers = () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
      if (intervalRef.current) clearInterval(intervalRef.current);
      intervalRef.current = null;
      if (ocrIntervalRef.current) clearInterval(ocrIntervalRef.current);
      ocrIntervalRef.current = null;
      if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
      setSecondsLeft(null);
    };

    const stopStream = () => {
      streamRef.current?.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    };

    const ensureMainAndExtraFrames = (): { mainFrame: string; extraFrames: string[] } => {
      const frames = ocrFramesRef.current.slice(0, ocrFramesTarget);
      if (frames.length === 0) {
        const captured = captureFrame();
        if (captured) frames.push(captured);
      }
      const mainFrame = frames[0] ?? "";
      const extraFrames = frames.slice(1, ocrFramesTarget);
      return { mainFrame, extraFrames };
    };

    const hasReachedDetectionMinDuration = () =>
      detectionStartedAtRef.current > 0 &&
      Date.now() - detectionStartedAtRef.current >= NO_QR_TIMEOUT_MS;

    const hasReachedDetectionTarget = () =>
      ocrFramesRef.current.length >= ocrFramesTarget && hasReachedDetectionMinDuration();

    const completeNoQr = () => {
      if (completedRef.current) return;
      completedRef.current = true;
      stopAllTimers();
      const { mainFrame, extraFrames } = ensureMainAndExtraFrames();
      const framesCaptured = ocrFramesRef.current.length;
      console.log(`${LOG_PREFIX} ${framesCaptured} frames capture took ${toSecs(performance.now() - detectionStartPerf)}`);
      console.log(
        `${LOG_PREFIX} no QR detected after ${toSecs(performance.now() - detectionStartPerf)}`
      );
      stopStream();
      if (!mainFrame) return;
      if (onTimeoutNoQrRef.current) onTimeoutNoQrRef.current(mainFrame, extraFrames);
    };

    const maybeCompleteSuccess = () => {
      if (completedRef.current) return;
      if (!qrUrlRef.current) return;
      if (!hasReachedDetectionTarget()) return;
      completedRef.current = true;
      stopAllTimers();
      const { mainFrame, extraFrames } = ensureMainAndExtraFrames();
      if (!mainFrame) return;
      const framesCaptured = ocrFramesRef.current.length;
      console.log(`${LOG_PREFIX} ${framesCaptured} frames capture took ${toSecs(performance.now() - detectionStartPerf)}`);
      console.log(
        `${LOG_PREFIX} QR detected after ${toSecs(performance.now() - detectionStartPerf)}`
      );
      if (navigator.vibrate) navigator.vibrate(100);
      stopStream();
      onQrFoundRef.current(mainFrame, qrUrlRef.current, extraFrames);
    };

    const firstFrame = captureFrame();
    if (firstFrame) {
      ocrFramesRef.current.push(firstFrame);
      flashCardFrame();
    }
    if (ocrFramesRef.current.length < ocrFramesTarget) {
      ocrIntervalRef.current = setInterval(() => {
        if (completedRef.current) return;
        if (ocrFramesRef.current.length >= ocrFramesTarget) {
          if (ocrIntervalRef.current) clearInterval(ocrIntervalRef.current);
          ocrIntervalRef.current = null;
          maybeCompleteSuccess();
          if (!qrUrlRef.current && hasReachedDetectionTarget()) completeNoQr();
          return;
        }
        const frame = captureFrame();
        if (!frame) return;
        ocrFramesRef.current.push(frame);
        flashCardFrame();
        if (ocrFramesRef.current.length >= ocrFramesTarget) {
          if (ocrIntervalRef.current) clearInterval(ocrIntervalRef.current);
          ocrIntervalRef.current = null;
          maybeCompleteSuccess();
          if (!qrUrlRef.current && hasReachedDetectionTarget()) completeNoQr();
        }
      }, EXTRA_FRAME_DELAY_MS);
    }

    intervalRef.current = setInterval(() => {
      if (completedRef.current) return;
      if (video.readyState !== video.HAVE_ENOUGH_DATA || video.videoWidth === 0) return;

      const vw = video.videoWidth;
      const vh = video.videoHeight;
      const scale = Math.min(1, MAX_CANVAS_SIZE / Math.max(vw, vh));
      const w = Math.round(vw * scale);
      const h = Math.round(vh * scale);
      canvas.width = w;
      canvas.height = h;
      ctx.drawImage(video, 0, 0, vw, vh, 0, 0, w, h);
      const cardW = Math.round(w * CARD_WIDTH_PCT);
      const cardH = Math.round(cardW / CARD_RATIO);
      const cardX = Math.round((w - cardW) / 2);
      const cardY = Math.round((h - cardH) / 2);
      const qrSize = Math.round(cardH * QR_ZONE_SIZE_PCT);
      const qrExtension = Math.round(cardW * BORDER_SHIFT_PCT);
      const qrX = cardX + cardW - qrSize - qrExtension;
      const qrY = cardY + cardH - qrSize;
      const qrW = qrSize + qrExtension;
      const qrImageData = ctx.getImageData(qrX, qrY, qrW, qrSize);
      const qrUrl = readQrFromImageData(qrImageData.data, qrW, qrSize);
      if (qrUrl) {
        console.log(`${LOG_PREFIX} QR decoded`);
        qrUrlRef.current = qrUrl;
        if (intervalRef.current) clearInterval(intervalRef.current);
        intervalRef.current = null;
        maybeCompleteSuccess();
      }
    }, SCAN_INTERVAL_MS);

    timeoutRef.current = setTimeout(() => {
      if (completedRef.current) return;
      maybeCompleteSuccess();
      if (completedRef.current) return;
      if (hasReachedDetectionTarget()) {
        completeNoQr();
      }
    }, NO_QR_TIMEOUT_MS);

    return () => {
      console.log(`${LOG_PREFIX} detection ended after ${toSecs(performance.now() - detectionStartPerf)}`);
      stopAllTimers();
    };
  }, [streamReady, error, detectionStarted]);

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center p-4 text-white">
        <p className="text-center">{error}</p>
        {onFallbackPhoto && (
          <button
            type="button"
            className="mt-4 px-4 py-2 bg-white text-gray-900 rounded"
            onClick={onFallbackPhoto}
          >
            Prendre une photo
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center w-full">
      {secondsLeft != null && (
        <div
          className="mb-2 px-3 py-1.5 rounded-full bg-white/20 text-white text-sm font-medium tabular-nums"
          role="status"
          aria-live="polite"
        >
          {secondsLeft} s
        </div>
      )}
      <div className="relative w-full flex justify-center rounded overflow-hidden" style={{ maxHeight: "70vh" }}>
        <video
          ref={videoRef}
          playsInline
          muted
          className="w-full h-full object-cover"
          style={{ maxHeight: "50vh" }}
        />
        <div
          className="absolute inset-0 flex items-center justify-center pointer-events-none"
          aria-hidden
        >
          <div
            ref={cardFrameRef}
            className={
              detectionStarted
                ? "scan-card-dim-outside relative border-2 border-yellow-400 rounded-md bg-transparent shadow-[0_0_0_1px_rgba(250,204,21,0.5)]"
                : "relative border-2 border-yellow-400 rounded-md bg-yellow-400/15 shadow-[0_0_0_1px_rgba(250,204,21,0.5)]"
            }
            style={{
              width: "90%",
              aspectRatio: String(CARD_RATIO),
              borderRadius: "5px",
            }}
          >
            {detectionStarted && (
              <svg
                className="absolute inset-0 w-full h-full pointer-events-none rounded-[5px]"
                aria-hidden
              >
                <defs>
                  <mask id={scanMaskId}>
                    <rect width="100%" height="100%" fill="white" />
                    <rect
                      x={`${OCR_LEFT_MARGIN_PCT * 100}%`}
                      y={`${(1 - OCR_HEIGHT_PCT) * 100}%`}
                      width={`${OCR_ZONE_WIDTH_PCT * 100}%`}
                      height={`${OCR_HEIGHT_PCT * 100}%`}
                      fill="black"
                      rx="2"
                    />
                    <rect
                      x={`${(1 - QR_ZONE_WIDTH_PCT) * 100}%`}
                      y={`${(1 - QR_ZONE_SIZE_PCT) * 100}%`}
                      width={`${QR_ZONE_WIDTH_PCT * 100}%`}
                      height={`${QR_ZONE_SIZE_PCT * 100}%`}
                      fill="black"
                      rx="2"
                    />
                  </mask>
                </defs>
                <rect
                  width="100%"
                  height="100%"
                  fill="rgba(0, 0, 0, 0.35)"
                  mask={`url(#${scanMaskId})`}
                />
              </svg>
            )}
            <div
              className={
                detectionStarted
                  ? "absolute bottom-0 z-10 border-2 border-amber-400 rounded-bl-md bg-transparent"
                  : "absolute bottom-0 border-2 border-amber-400/90 rounded-bl-md bg-amber-500/20"
              }
              style={{
                left: `${OCR_LEFT_MARGIN_PCT * 100}%`,
                height: `${OCR_HEIGHT_PCT * 100}%`,
                width: `${OCR_ZONE_WIDTH_PCT * 100}%`,
              }}
              title="Zone OCR"
            >
              <span className="absolute bottom-1 left-1 text-xs font-medium text-amber-200 drop-shadow-sm">
                Infos travailleur
              </span>
            </div>
            <div
              className={
                detectionStarted
                  ? "absolute right-0 bottom-0 z-10 border-2 border-green-400 rounded-bl-md bg-transparent"
                  : "absolute right-0 bottom-0 border-2 border-green-400 rounded-bl-md bg-green-500/20"
              }
              style={{
                height: `${QR_ZONE_SIZE_PCT * 100}%`,
                width: `${QR_ZONE_WIDTH_PCT * 100}%`,
              }}
              title="Zone QR"
            >
              <span className="absolute bottom-1 right-1 text-xs font-medium text-green-200 drop-shadow-sm">
                QR
              </span>
            </div>
          </div>
        </div>
      </div>
      <canvas ref={canvasRef} className="hidden" />
      <p className="text-white text-center text-sm mt-2 px-2">
        Alignez la carte BTP dans le cadre
      </p>
      {!detectionStarted && (
        <button
          type="button"
          className="mt-4 px-4 py-2 bg-yellow-400 text-gray-900 rounded text-sm font-medium disabled:opacity-50"
          onClick={() => setDetectionStarted(true)}
          disabled={!streamReady}
        >
          Démarrer la détection
        </button>
      )}
      {onFallbackPhoto && (
        <button
          type="button"
          className="mt-4 px-4 py-2 bg-white text-gray-900 rounded text-sm"
          onClick={onFallbackPhoto}
        >
          Prendre une photo à la place
        </button>
      )}
    </div>
  );
};

export default LiveQrScanner;
